'use client';

import React, { useState, useEffect, useRef } from 'react';
import Image from 'next/image';
import Link from 'next/link';
import { Globe, BookOpen, Users, Award, Clock, ChevronRight, Star, Zap, Shield, TrendingUp, Download, ShoppingBag, MessageCircle, Phone, MapPin, GraduationCap, Briefcase, X, Menu } from 'lucide-react';
import ImageGallery from './components/ImageGallery';
import PopupNotification from './components/PopupNotification';
import { createClient } from '@/lib/supabase/client';

interface HomeCourse {
  id: string;
  code: string;
  name: string;
  duration: string;
  fee: number;
  category: string;
}

const DIGITAL_PRODUCTS = [
  { name: 'Tally Prime Complete Guide', price: '₹299', icon: '📄', link: '#store' },
  { name: 'ADCA Video Lectures', price: '₹1,499', icon: '🎬', link: '#store' },
  { name: 'MS Office Practice Tests', price: '₹199', icon: '📝', link: '#store' },
];

interface SiteSettings {
  contact_phone: string;
  contact_email: string;
  contact_address: string;
  contact_website: string;
  contact_whatsapp: string;
  gps_lat: string;
  gps_lng: string;
  gps_map_embed: string;
  gps_address_label: string;
  logo_url: string;
  institute_name: string;
}

const DEFAULT_SITE_SETTINGS: SiteSettings = {
  contact_phone: '08443-9900658291 / 08443358053',
  contact_email: 'admin@yashcomputers.com',
  contact_address: 'Shorapur, Yadgir, Karnataka, India',
  contact_website: 'www.yashcomputers.com',
  contact_whatsapp: '9900658291',
  gps_lat: '16.5226',
  gps_lng: '76.7606',
  gps_map_embed: 'https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d3836.5!2d76.7606!3d16.5226!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x3bc7b3b3b3b3b3b3%3A0x0!2sYASH+Computers+KEONICS+Shorapur!5e0!3m2!1sen!2sin!4v1699999999999!5m2!1sen!2sin',
  gps_address_label: 'YASH Computers — KEONICS, Shorapur, Yadgir Dist., Karnataka',
  logo_url: '/assets/images/logo1-1786300024701.jpg',
  institute_name: 'YASH Computers',
};

function AnimatedCounter({ target, suffix = '' }: { target: number; suffix?: string }) {
  const [count, setCount] = useState(0);
  const ref = useRef<HTMLDivElement>(null);
  const started = useRef(false);

  useEffect(() => {
    const observer = new IntersectionObserver(([entry]) => {
      if (entry.isIntersecting && !started.current) {
        started.current = true;
        let start = 0;
        const step = Math.ceil(target / 60);
        const timer = setInterval(() => {
          start += step;
          if (start >= target) { setCount(target); clearInterval(timer); } else setCount(start);
        }, 25);
      }
    });
    if (ref.current) observer.observe(ref.current);
    return () => observer.disconnect();
  }, [target]);

  return <div ref={ref}>{count}{suffix}</div>;
}

export default function HomePage() {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [showAdmissionsPopup, setShowAdmissionsPopup] = useState(false);
  const [siteSettings, setSiteSettings] = useState<SiteSettings>(DEFAULT_SITE_SETTINGS);
  const [liveCourses, setLiveCourses] = useState<HomeCourse[]>([]);

  useEffect(() => {
    const timer = setTimeout(() => setShowAdmissionsPopup(true), 2500);
    return () => clearTimeout(timer);
  }, []);

  // Load site settings from Supabase
  useEffect(() => {
    const loadSettings = async () => {
      try {
        const supabase = createClient();
        const { data, error } = await supabase
          .from('site_settings')
          .select('key, value');
        if (error) return;
        if (data && data.length > 0) {
          const settingsMap: Partial<SiteSettings> = {};
          data.forEach((row: { key: string; value: string }) => {
            (settingsMap as Record<string, string>)[row.key] = row.value || '';
          });
          setSiteSettings(prev => ({ ...prev, ...settingsMap }));
        }
      } catch {
        // Use defaults silently
      }
    };
    loadSettings();
  }, []);

  // Load active courses from Supabase
  useEffect(() => {
    const loadCourses = async () => {
      try {
        const supabase = createClient();
        const { data, error } = await supabase
          .from('courses')
          .select('id, code, name, duration, fee, category')
          .eq('active', true)
          .order('name', { ascending: true });
        if (error) return;
        if (data && data.length > 0) {
          setLiveCourses(data.map((c: { id: string; code: string; name: string; duration: string; fee: number; category: string }) => ({
            id: c.id,
            code: c.code || '',
            name: c.name || '',
            duration: c.duration || '',
            fee: c.fee || 0,
            category: c.category || 'Computer',
          })));
        }
      } catch {
        // Use static fallback silently
      }
    };
    loadCourses();
  }, []);

  const waLink = `https://wa.me/91${siteSettings.contact_whatsapp}?text=Hello%20YASH%20Computers%2C%20I%20would%20like%20to%20know%20more%20about%20your%20courses.`;
  const mapsLink = `https://maps.google.com/?q=${siteSettings.gps_lat},${siteSettings.gps_lng}`;

  const navLinks = ['Home', 'About', 'Courses', 'Admission', 'Gallery', 'Blog', 'Contact'];

  return (
    <div className="min-h-screen bg-white">

      {/* ── TOP NAVIGATION ── */}
      <header className="sticky top-0 z-50 bg-white border-b border-gray-100 shadow-sm">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-between h-16">

            {/* Logo — dynamic from admin */}
            <Link href="/" className="flex items-center gap-3 flex-shrink-0">
              <div className="relative w-10 h-10 rounded-xl overflow-hidden flex-shrink-0"
                style={{ background: '#1E3A5F' }}>
                {siteSettings.logo_url ? (
                  // eslint-disable-next-line @next/next/no-img-element
                  <img
                    src={siteSettings.logo_url}
                    alt={`${siteSettings.institute_name} Logo`}
                    className="w-full h-full object-contain p-1"
                  />
                ) : (
                  <Image
                    src="/assets/images/logo1-1786300024701.jpg"
                    alt="YASH Computers KEONICS Logo"
                    fill
                    className="object-contain p-1"
                  />
                )}
              </div>
              <div>
                <div className="font-bold text-base leading-tight" style={{ color: '#1E3A5F' }}>
                  {siteSettings.institute_name || 'YASH Computers'}
                </div>
                <div className="text-xs text-gray-500 leading-tight">KEONICS Franchisee</div>
              </div>
            </Link>

            {/* Nav Links - Desktop */}
            <nav className="hidden lg:flex items-center gap-1">
              {navLinks.map((item, idx) => (
                <a
                  key={item}
                  href={`#${item.toLowerCase()}`}
                  className="px-3 py-2 text-sm font-medium rounded-lg transition-colors"
                  style={{
                    color: idx === 0 ? '#2563EB' : '#374151',
                  }}
                  onMouseEnter={e => (e.currentTarget.style.color = '#2563EB')}
                  onMouseLeave={e => (e.currentTarget.style.color = idx === 0 ? '#2563EB' : '#374151')}
                >
                  {item}
                </a>
              ))}
              <a href="#" className="px-3 py-2 text-sm font-medium text-gray-700 flex items-center gap-1">
                More <ChevronRight size={14} className="rotate-90" />
              </a>
            </nav>

            {/* Right Controls */}
            <div className="flex items-center gap-3">
              <a
                href={`tel:${siteSettings.contact_phone}`}
                className="hidden md:flex items-center gap-1.5 text-sm font-medium"
                style={{ color: '#374151' }}
              >
                <Phone size={15} style={{ color: '#1E3A5F' }} />
                {siteSettings.contact_phone}
              </a>
              <button className="hidden md:flex items-center justify-center w-8 h-8 rounded-full border border-gray-200 text-gray-500 hover:bg-gray-50">
                <span className="text-base">🌙</span>
              </button>
              <Link
                href="/sign-up-login-screen"
                className="flex items-center gap-1.5 px-4 py-2 rounded-lg text-sm font-semibold text-white transition-all hover:opacity-90"
                style={{ background: '#2563EB' }}
              >
                <GraduationCap size={15} />
                Login
              </Link>
              <button
                className="lg:hidden p-2 rounded-lg text-gray-600"
                onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
              >
                <Menu size={20} />
              </button>
            </div>
          </div>
        </div>

        {/* Mobile Menu */}
        {mobileMenuOpen && (
          <div className="lg:hidden border-t border-gray-100 bg-white px-4 py-3">
            {navLinks.map(item => (
              <a
                key={item}
                href={`#${item.toLowerCase()}`}
                className="block py-2 text-sm font-medium text-gray-700"
                onClick={() => setMobileMenuOpen(false)}
              >
                {item}
              </a>
            ))}
          </div>
        )}
      </header>

      {/* ── ORANGE ANNOUNCEMENT RIBBON ── */}
      <div className="w-full py-2.5 text-center text-sm font-semibold" style={{ background: '#F59E0B', color: '#1a1a1a' }}>
        Admissions Open for 2026 Batch — Early-bird fee concession available&nbsp;
        <Link href="/student-application" className="underline font-bold hover:opacity-80">Apply Now</Link>
      </div>

      {/* ── HERO SECTION ── */}
      <section
        id="home"
        className="relative overflow-hidden"
        style={{
          background: 'linear-gradient(135deg, #f0f4ff 0%, #fef9f0 50%, #f0f7ff 100%)',
          minHeight: '580px',
        }}
      >
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16 lg:py-20">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">

            {/* Left Column — Text */}
            <div>
              <div className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full text-xs font-semibold mb-6"
                style={{ background: 'rgba(30,58,95,0.08)', border: '1px solid rgba(30,58,95,0.2)', color: '#1E3A5F' }}>
                <Shield size={12} />
                KEONICS Franchisee · Government of Karnataka Enterprise
              </div>

              <h1 className="text-4xl sm:text-5xl font-extrabold leading-tight mb-5" style={{ color: '#111827' }}>
                Empowering Careers<br />
                Through{' '}
                <span style={{ color: '#2563EB' }}>Computer<br />Education</span>
                {' '}Since 1996
              </h1>

              <p className="text-base text-gray-600 mb-8 leading-relaxed max-w-lg">
                YASH Computers, Shorapur is a KEONICS Franchisee institute that has trained 15,000+ students in computer skills, accounting, programming and digital marketing — with 4,600+ placed in rewarding jobs.
              </p>

              <div className="flex flex-wrap gap-3 mb-8">
                <Link
                  href="/sign-up-login-screen"
                  className="flex items-center gap-2 px-6 py-3 rounded-lg text-sm font-bold text-white transition-all hover:opacity-90 shadow-md"
                  style={{ background: '#1E3A5F' }}
                >
                  Apply for Admission →
                </Link>
                <a
                  href="#courses"
                  className="flex items-center gap-2 px-6 py-3 rounded-lg text-sm font-semibold border-2 transition-all hover:bg-gray-50"
                  style={{ borderColor: '#1E3A5F', color: '#1E3A5F' }}
                >
                  Explore Courses
                </a>
              </div>

              {/* Location & Phone — dynamic */}
              <div className="flex flex-wrap items-center gap-5 text-sm text-gray-500">
                <span className="flex items-center gap-1.5">
                  <MapPin size={14} style={{ color: '#1E3A5F' }} />
                  {siteSettings.contact_address}
                </span>
                <a href={`tel:${siteSettings.contact_phone}`} className="flex items-center gap-1.5 hover:text-blue-600 transition-colors">
                  <Phone size={14} style={{ color: '#1E3A5F' }} />
                  {siteSettings.contact_phone}
                </a>
              </div>
            </div>

            {/* Right Column — Classroom Photo */}
            <div className="relative">
              <div className="relative rounded-2xl overflow-hidden shadow-2xl"
                style={{ aspectRatio: '4/3' }}>
                <Image
                  src="/assets/images/IMG-20250501-WA0005-1786297878800.jpg"
                  alt="Students learning computer skills at YASH Computers KEONICS classroom"
                  fill
                  className="object-cover"
                  priority
                />
                <div className="absolute inset-0"
                  style={{ background: 'linear-gradient(to bottom, transparent 60%, rgba(30,58,95,0.15) 100%)' }} />
              </div>

              <div className="absolute -top-4 -right-4 bg-white rounded-2xl shadow-xl px-4 py-3 flex items-center gap-3"
                style={{ border: '1px solid #e5e7eb', minWidth: '160px' }}>
                <div className="w-10 h-10 rounded-xl flex items-center justify-center flex-shrink-0"
                  style={{ background: '#EFF6FF' }}>
                  <Users size={20} style={{ color: '#2563EB' }} />
                </div>
                <div>
                  <div className="text-xl font-extrabold leading-none" style={{ color: '#111827' }}>15,000+</div>
                  <div className="text-xs text-gray-500 mt-0.5">students trained</div>
                </div>
              </div>

              <div className="absolute -bottom-4 -left-4 bg-white rounded-2xl shadow-xl px-4 py-3 flex items-center gap-3"
                style={{ border: '1px solid #e5e7eb' }}>
                <div className="w-10 h-10 rounded-xl flex items-center justify-center flex-shrink-0"
                  style={{ background: '#FEF3C7' }}>
                  <Award size={20} style={{ color: '#F59E0B' }} />
                </div>
                <div>
                  <div className="text-xl font-extrabold leading-none" style={{ color: '#111827' }}>30 Years</div>
                  <div className="text-xs text-gray-500 mt-0.5">of trusted excellence</div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ── STAT COUNTERS ROW ── */}
      <section className="py-8 bg-white border-b border-gray-100">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
            {[
              { icon: <Clock size={24} />, value: 30, suffix: '+', label: 'Years of Experience', iconBg: '#EFF6FF', iconColor: '#2563EB' },
              { icon: <Users size={24} />, value: 15000, suffix: '+', label: 'Students Trained', iconBg: '#EFF6FF', iconColor: '#2563EB' },
              { icon: <Briefcase size={24} />, value: 4600, suffix: '+', label: 'Students Placed', iconBg: '#FEF3C7', iconColor: '#F59E0B' },
              { icon: <BookOpen size={24} />, value: 16, suffix: '', label: 'Courses Offered', iconBg: '#EFF6FF', iconColor: '#2563EB' },
            ].map((stat) => (
              <div
                key={stat.label}
                className="flex items-center gap-4 p-5 rounded-2xl border border-gray-100 bg-white shadow-sm hover:shadow-md transition-shadow"
              >
                <div className="w-12 h-12 rounded-xl flex items-center justify-center flex-shrink-0"
                  style={{ background: stat.iconBg }}>
                  <span style={{ color: stat.iconColor }}>{stat.icon}</span>
                </div>
                <div>
                  <div className="text-2xl font-extrabold leading-none" style={{ color: '#111827' }}>
                    <AnimatedCounter target={stat.value} suffix={stat.suffix} />
                  </div>
                  <div className="text-xs text-gray-500 mt-1">{stat.label}</div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── COURSES SECTION ── */}
      <section id="courses" className="py-16 px-4 sm:px-6 lg:px-8 bg-gray-50">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-10">
            <h2 className="text-3xl font-extrabold mb-3" style={{ color: '#1E3A5F' }}>Our Courses</h2>
            <p className="text-gray-500">Industry-recognized certifications for your career growth</p>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
            {liveCourses.map((course) => (
              <div
                key={course.id}
                className="rounded-2xl p-5 bg-white border border-gray-100 shadow-sm hover:shadow-md hover:-translate-y-0.5 transition-all duration-200 cursor-pointer group"
              >
                <div className="flex items-center gap-2 mb-3">
                  <span
                    className="text-xs font-bold px-2.5 py-1 rounded-lg"
                    style={{ background: '#EFF6FF', color: '#2563EB' }}
                  >
                    {course.code}
                  </span>
                </div>
                <h3 className="text-sm font-semibold text-gray-800 mb-3 leading-snug">{course.name}</h3>
                <div className="flex items-center justify-between text-xs text-gray-500">
                  <span className="flex items-center gap-1"><Clock size={11} /> {course.duration}</span>
                  <span className="font-bold" style={{ color: '#1E3A5F' }}>₹{course.fee.toLocaleString('en-IN')}</span>
                </div>
                <Link
                  href="/sign-up-login-screen"
                  className="mt-3 w-full flex items-center justify-center gap-1.5 py-2 rounded-xl text-xs font-semibold transition-all opacity-0 group-hover:opacity-100"
                  style={{ background: '#EFF6FF', color: '#2563EB' }}
                >
                  Enroll <ChevronRight size={12} />
                </Link>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── DIGITAL STORE ── */}
      <section id="store" className="py-16 px-4 sm:px-6 lg:px-8 bg-white">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-10">
            <h2 className="text-3xl font-extrabold mb-3" style={{ color: '#1E3A5F' }}>Digital Store</h2>
            <p className="text-gray-500">Study materials, video courses & practice tests — instant download after payment</p>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-5">
            {DIGITAL_PRODUCTS.map((prod, i) => (
              <div
                key={`dp-${i}`}
                className="rounded-2xl p-6 border border-gray-100 shadow-sm hover:shadow-md hover:-translate-y-0.5 transition-all duration-200"
              >
                <div className="text-3xl mb-4">{prod.icon}</div>
                <h3 className="font-semibold text-gray-800 text-sm mb-3">{prod.name}</h3>
                <div className="flex items-center justify-between">
                  <span className="text-lg font-bold" style={{ color: '#1E3A5F' }}>{prod.price}</span>
                  <Link
                    href="/sign-up-login-screen"
                    className="flex items-center gap-1.5 px-3 py-1.5 rounded-xl text-xs font-semibold text-white"
                    style={{ background: '#2563EB' }}
                  >
                    <Download size={12} /> Buy Now
                  </Link>
                </div>
              </div>
            ))}
          </div>
          <div className="text-center mt-8">
            <Link
              href="/sign-up-login-screen"
              className="inline-flex items-center gap-2 px-6 py-3 rounded-xl font-semibold text-sm border-2 transition-all hover:bg-blue-50"
              style={{ borderColor: '#1E3A5F', color: '#1E3A5F' }}
            >
              <ShoppingBag size={16} /> View All Products
            </Link>
          </div>
        </div>
      </section>

      {/* ── WHY CHOOSE US ── */}
      <section className="py-16 px-4 sm:px-6 lg:px-8 bg-gray-50">
        <div className="max-w-5xl mx-auto">
          <div className="text-center mb-10">
            <h2 className="text-3xl font-extrabold mb-3" style={{ color: '#1E3A5F' }}>Why Choose YASH Computers?</h2>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
            {[
              { icon: <Zap size={20} />, title: 'Real-Time Attendance', desc: 'Staff marks attendance instantly — students and parents get WhatsApp alerts automatically', color: '#2563EB' },
              { icon: <Shield size={20} />, title: 'Secure Fee Management', desc: 'Digital fee receipts, payment tracking, and automated reminders via WhatsApp', color: '#1E3A5F' },
              { icon: <Star size={20} />, title: 'Expert Faculty', desc: 'Experienced instructors with industry expertise and hands-on training approach', color: '#F59E0B' },
              { icon: <TrendingUp size={20} />, title: 'Career Placement', desc: '95% placement assistance with resume building and interview preparation', color: '#2563EB' },
              { icon: <BookOpen size={20} />, title: 'Flexible Batches', desc: 'Morning, afternoon, and evening batches to suit your schedule', color: '#1E3A5F' },
              { icon: <Award size={20} />, title: 'Certified Courses', desc: 'Government-recognized certifications accepted by top employers nationwide', color: '#F59E0B' },
            ].map((item) => (
              <div
                key={item.title}
                className="rounded-2xl p-5 bg-white border border-gray-100 shadow-sm hover:shadow-md transition-shadow"
              >
                <div className="w-10 h-10 rounded-xl flex items-center justify-center mb-3"
                  style={{ background: item.color === '#F59E0B' ? '#FEF3C7' : '#EFF6FF' }}>
                  <span style={{ color: item.color }}>{item.icon}</span>
                </div>
                <h3 className="font-semibold text-gray-800 mb-2 text-sm">{item.title}</h3>
                <p className="text-xs text-gray-500 leading-relaxed">{item.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── IMAGE GALLERY ── */}
      <div id="gallery" className="bg-white">
        <ImageGallery />
      </div>

      {/* ── CONTACT SECTION — dynamic ── */}
      <section id="contact" className="py-16 px-4 sm:px-6 lg:px-8 bg-gray-50">
        <div className="max-w-3xl mx-auto text-center">
          <h2 className="text-3xl font-extrabold mb-8" style={{ color: '#1E3A5F' }}>Contact Us</h2>
          <div className="rounded-2xl p-8 bg-white border border-gray-100 shadow-sm">
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-6">
              <a
                href={`https://${siteSettings.contact_website}`}
                target="_blank"
                rel="noopener noreferrer"
                className="flex flex-col items-center gap-2 p-4 rounded-xl hover:bg-blue-50 transition-colors"
              >
                <Globe size={24} style={{ color: '#2563EB' }} />
                <span className="text-xs text-gray-500">Website</span>
                <span className="text-sm font-semibold" style={{ color: '#2563EB' }}>{siteSettings.contact_website}</span>
              </a>
              <a
                href={`tel:${siteSettings.contact_phone}`}
                className="flex flex-col items-center gap-2 p-4 rounded-xl hover:bg-blue-50 transition-colors"
              >
                <Phone size={24} style={{ color: '#1E3A5F' }} />
                <span className="text-xs text-gray-500">Phone</span>
                <span className="text-sm font-semibold" style={{ color: '#1E3A5F' }}>{siteSettings.contact_phone}</span>
              </a>
              <a
                href={waLink}
                target="_blank"
                rel="noopener noreferrer"
                className="flex flex-col items-center gap-2 p-4 rounded-xl hover:bg-green-50 transition-colors"
              >
                <MessageCircle size={24} style={{ color: '#25D366' }} />
                <span className="text-xs text-gray-500">WhatsApp Business</span>
                <span className="text-sm font-semibold" style={{ color: '#25D366' }}>Chat Now</span>
              </a>
            </div>
            {/* Email row */}
            <div className="mt-4 pt-4 border-t border-gray-100">
              <a
                href={`mailto:${siteSettings.contact_email}`}
                className="flex items-center justify-center gap-2 text-sm text-gray-600 hover:text-blue-600 transition-colors"
              >
                <span className="text-gray-400">✉</span>
                {siteSettings.contact_email}
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* ── GOOGLE MAP — dynamic GPS ── */}
      <section id="location" className="py-16 px-4 sm:px-6 lg:px-8 bg-white">
        <div className="max-w-5xl mx-auto">
          <div className="text-center mb-8">
            <h2 className="text-3xl font-extrabold mb-3" style={{ color: '#1E3A5F' }}>Find Us</h2>
            <p className="text-gray-500">Visit YASH Computers — KEONICS Affiliated Computer Education Center</p>
          </div>
          <div className="rounded-2xl overflow-hidden border border-gray-100 shadow-md">
            <div className="relative w-full" style={{ paddingBottom: '45%', minHeight: '280px' }}>
              <iframe
                src={siteSettings.gps_map_embed}
                className="absolute inset-0 w-full h-full"
                style={{ border: 0 }}
                allowFullScreen
                loading="lazy"
                referrerPolicy="no-referrer-when-downgrade"
                title="YASH Computers Location — KEONICS, Karnataka"
              />
            </div>
            <div className="px-6 py-4 flex flex-col sm:flex-row items-start sm:items-center justify-between gap-3 bg-white border-t border-gray-100">
              <div className="flex items-start gap-3">
                <div className="mt-0.5 p-2 rounded-xl flex-shrink-0" style={{ background: '#EFF6FF' }}>
                  <MapPin size={16} style={{ color: '#1E3A5F' }} />
                </div>
                <div>
                  <div className="text-sm font-semibold text-gray-800">{siteSettings.gps_address_label}</div>
                  <div className="text-xs text-gray-500 mt-0.5">{siteSettings.contact_address}</div>
                </div>
              </div>
              <a
                href={mapsLink}
                target="_blank"
                rel="noopener noreferrer"
                className="flex items-center gap-1.5 px-4 py-2 rounded-xl text-xs font-semibold text-white transition-all hover:opacity-90 flex-shrink-0"
                style={{ background: '#1E3A5F' }}
              >
                <Globe size={13} />
                Open in Google Maps
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* ── FOOTER — dynamic logo ── */}
      <footer className="py-8 px-4 bg-gray-50 border-t border-gray-100">
        <div className="max-w-7xl mx-auto flex flex-col sm:flex-row items-center justify-between gap-4">
          <div className="flex items-center gap-3">
            <div className="relative w-8 h-8 rounded-lg overflow-hidden" style={{ background: '#1E3A5F' }}>
              {siteSettings.logo_url ? (
                // eslint-disable-next-line @next/next/no-img-element
                <img
                  src={siteSettings.logo_url}
                  alt={`${siteSettings.institute_name} Logo`}
                  className="w-full h-full object-contain p-0.5"
                />
              ) : (
                <Image
                  src="/assets/images/logo1-1786300024701.jpg"
                  alt="YASH Computers Logo"
                  fill
                  className="object-contain p-0.5"
                />
              )}
            </div>
            <span className="text-sm font-semibold" style={{ color: '#1E3A5F' }}>{siteSettings.institute_name}</span>
          </div>
          <p className="text-xs text-gray-400 text-center">
            © 2026 {siteSettings.institute_name}. All rights reserved. | KEONICS Affiliated | {siteSettings.contact_website}
          </p>
          <div className="flex items-center gap-4">
            <a href={`https://${siteSettings.contact_website}`} target="_blank" rel="noopener noreferrer"
              className="text-xs text-gray-500 hover:text-blue-600 transition-colors">
              Website
            </a>
            <a href={waLink} target="_blank" rel="noopener noreferrer"
              className="text-xs text-gray-500 hover:text-green-600 transition-colors">
              WhatsApp
            </a>
          </div>
        </div>
      </footer>

      {/* ── ADMISSIONS POPUP CARD ── */}
      {showAdmissionsPopup && (
        <div
          className="fixed bottom-24 right-6 z-50 bg-white rounded-2xl shadow-2xl overflow-hidden"
          style={{ width: '260px', border: '1px solid #e5e7eb' }}
        >
          <div className="flex items-center justify-between px-4 py-3"
            style={{ background: '#1E3A5F' }}>
            <div className="flex items-center gap-2">
              <GraduationCap size={16} className="text-white" />
              <span className="text-sm font-bold text-white">Admissions Open 2026</span>
            </div>
            <button
              onClick={() => setShowAdmissionsPopup(false)}
              className="text-white/70 hover:text-white transition-colors"
            >
              <X size={16} />
            </button>
          </div>
          <div className="px-4 py-3">
            <p className="text-xs text-gray-500 mb-3">Apply online in minutes</p>
            <Link
              href="/student-application"
              className="block w-full text-center py-2.5 rounded-xl text-sm font-bold text-white transition-all hover:opacity-90"
              style={{ background: '#2563EB' }}
              onClick={() => setShowAdmissionsPopup(false)}
            >
              Apply Now
            </Link>
          </div>
        </div>
      )}

      {/* ── FLOATING WHATSAPP BUTTON ── */}
      <a
        href={waLink}
        target="_blank"
        rel="noopener noreferrer"
        className="fixed bottom-6 right-6 z-50 flex items-center gap-2 px-4 py-3 rounded-2xl font-semibold text-sm shadow-2xl transition-all hover:scale-110"
        style={{ background: '#25D366', color: '#fff', boxShadow: '0 4px 24px rgba(37,211,102,0.4)' }}
        title="Chat on WhatsApp Business"
      >
        <MessageCircle size={20} />
        <span className="hidden sm:inline">WhatsApp</span>
      </a>

      {/* ── OFFER / EVENT POPUP NOTIFICATION ── */}
      <PopupNotification />
    </div>
  );
}